import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { $usersStore } from '@lib/store';
import { cn, PermissionUtils } from '@shared/utils';
import { UserType, type UserWithPaymentInfo } from '@shared/api';
import { SmallAccount } from './smallAccount';
import { SmallCard } from './smallCard';
import { AddAccountsModal, AddCardsModal } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import AccountIcon from '@assets/icons/account.svg';
import CardIcon from '@assets/icons/card.svg';
import PlusIcon from '@assets/icons/plus.svg';

interface UserLongInfoProps {
  className?: string;
  user: UserWithPaymentInfo;
}

export const UserLongInfo = (props: UserLongInfoProps) => {
  const [showAddAccounts, setShowAddAccounts] = useState<boolean>(false);
  const [showAddCards, setShowAddCards] = useState<boolean>(false);

  const { t } = useTranslation('userManagement');

  const removeAccountHandler = (id: number) => $usersStore.events.removeAccount({ userIds: props.user.id, accountIds: [id] });
  const removeCardHandler = (id: number) => $usersStore.events.removeCard({ userIds: props.user.id, cardIds: [id] });

  if (props.user.userType === UserType.Admin) return null;
  return (
    <div
      className={cn(
        'border-t border-t-gray-black px-6 pb-8 pt-5',
        props.user.locked && 'pointer-events-none select-none opacity-50',
        props.className,
      )}
    >
      <div className='mb-6 flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-2'>
            <Icon
              className='flex h-8 w-8 flex-none items-center justify-center bg-ice dark:bg-eerie-black'
              color='secondary-inverse'
              icon={<AccountIcon />}
            />
            <Text weight='medium' size='sm'>
              {t('user.info.userType.manager', { count: props.user.accounts?.length })}
            </Text>
          </div>
          {props.user.userType === UserType.User && (
            <div className='flex items-center gap-2'>
              <Icon
                className='flex h-8 w-8 flex-none items-center justify-center bg-ice dark:bg-eerie-black'
                color='secondary-inverse'
                icon={<CardIcon />}
              />
              <Text weight='medium' size='sm'>
                {t('user.info.userType.user', { count: props.user.cards?.length })}
              </Text>
            </div>
          )}
        </div>
        {PermissionUtils.manageAccountsPermissions() && props.user.userType === UserType.Manager && (
          <Button
            className='flex h-8 w-8 flex-none items-center justify-center rounded p-0 text-center'
            color='outlined'
            onClick={() => setShowAddAccounts(true)}
          >
            <Icon icon={<PlusIcon />} color='inherit' />
          </Button>
        )}
        {(PermissionUtils.manageAccountsPermissions() || PermissionUtils.manageCardsPermissions()) && props.user.userType === UserType.User && (
          <Button
            className='flex h-8 w-8 flex-none items-center justify-center rounded p-0 text-center'
            color='outlined'
            onClick={() => setShowAddCards(true)}
          >
            <Icon icon={<PlusIcon />} color='inherit' />
          </Button>
        )}
      </div>
      <div className='flex flex-col gap-6'>
        {props.user.accounts &&
          props.user.accounts.map((account) => <SmallAccount key={account.id} account={account} onRemove={removeAccountHandler} />)}
        {props.user.cards && props.user.cards.map((card) => <SmallCard key={card.cardId} card={card} onRemove={removeCardHandler} />)}
      </div>
      <AddAccountsModal
        isOpen={showAddAccounts}
        user={props.user}
        accountsItems={props.user.accounts}
        onClose={() => setShowAddAccounts(false)}
        onSubmit={(data) => $usersStore.events.addAccount(data)}
      />
      <AddCardsModal
        isOpen={showAddCards}
        user={props.user}
        accountsItems={props.user.accounts}
        cardsItems={props.user.cards}
        onClose={() => setShowAddCards(false)}
        onSubmit={(data) =>
          Promise.all([
            data.accountIds.length > 0 && $usersStore.events.addAccount(data),
            data.cardIds.length > 0 && $usersStore.events.addCard(data),
          ])
        }
      />
    </div>
  );
};
